import type { Tier } from '@/state/GameState';
import type { Anmeldelse, ContractedBook } from '@/data/types';

export const TIER_ORDEN: ReadonlyArray<Tier> = ['D', 'C', 'B', 'A', 'S'];

export const TIER_NAVN: Record<Tier, string> = {
  D: 'Indie-start',
  C: 'Etablert',
  B: 'Anerkjent',
  A: 'Stor-tre-kandidat',
  S: 'Stortrekanten',
};

export const BESTSELGER_GRENSE = 100_000;
export const BRAGE_NOMINASJON_SNITT = 5.0;
export const BRAGE_VINNER_SNITT = 6.0;
export const STERK_BOK_SNITT = 5.0;
export const ANMELDELSER_FOR_VURDERING = 4;

export type TierKriterium = {
  navn: string;
  naadd: number;
  krav: number;
};

export type TierKravStatus = {
  kriterier: ReadonlyArray<TierKriterium>;
  klart: boolean;
};

export type TierBerregnetData = {
  antallUtgivelser: number;
  totalInntekt: number;
  bestePoengSnitt: number;
  bestselger: number;
  sterkeBoker: number;
  brageVinnere: number;
  bestselgere: number;
  brageNominasjoner: number;
  bragePriser: number;
  andrePriser: number;
  priserTotal: number;
  internasjonaleAvtaler: number;
  filmRettigheter: number;
  lydbokAvtaler: number;
};

export function harXAntallPriser(
  data: TierBerregnetData,
  n: number,
): boolean {
  return data.priserTotal >= n;
}

export function harXInternasjonaleAvtaler(
  data: TierBerregnetData,
  n: number,
): boolean {
  return data.internasjonaleAvtaler >= n;
}

export function harFilmRettigheter(
  data: TierBerregnetData,
  n: number,
): boolean {
  return data.filmRettigheter >= n;
}

export function harLydbokAvtaler(
  data: TierBerregnetData,
  n: number,
): boolean {
  return data.lydbokAvtaler >= n;
}

export function naestTier(tier: Tier): Tier | null {
  const idx = TIER_ORDEN.indexOf(tier);
  if (idx === -1) return null;
  if (idx === TIER_ORDEN.length - 1) return null;
  return TIER_ORDEN[idx + 1];
}

export function snittTerninger(anmeldelser: ReadonlyArray<Anmeldelse>): number {
  if (anmeldelser.length === 0) return 0;
  const sum = anmeldelser.reduce((s, a) => s + a.terninger, 0);
  return sum / anmeldelser.length;
}

export function beregnTierData(
  utgitteBoker: ReadonlyArray<ContractedBook>,
  iProduksjonUtgitte: ReadonlyArray<ContractedBook>,
  brageNominasjoner = 0,
  bragePriser = 0,
  andrePriser = 0,
  internasjonaleAvtaler = 0,
  filmRettigheter = 0,
  lydbokAvtaler = 0,
): TierBerregnetData {
  const alle: ContractedBook[] = [...utgitteBoker, ...iProduksjonUtgitte];
  const antallUtgivelser = alle.length;
  const totalInntekt = alle.reduce((s, b) => s + b.inntekt, 0);

  let bestePoengSnitt = 0;
  let bestselger = 0;
  let sterkeBoker = 0;
  let brageVinnere = 0;
  let bestselgere = 0;

  for (const bok of alle) {
    if (bok.salg > bestselger) bestselger = bok.salg;
    if (bok.salg >= BESTSELGER_GRENSE) bestselgere += 1;
    if (bok.anmeldelser.length >= ANMELDELSER_FOR_VURDERING) {
      const snitt = snittTerninger(bok.anmeldelser);
      if (snitt > bestePoengSnitt) bestePoengSnitt = snitt;
      if (snitt >= STERK_BOK_SNITT) sterkeBoker += 1;
      if (snitt >= BRAGE_VINNER_SNITT) brageVinnere += 1;
    }
  }

  return {
    antallUtgivelser,
    totalInntekt,
    bestePoengSnitt,
    bestselger,
    sterkeBoker,
    brageVinnere,
    bestselgere,
    brageNominasjoner,
    bragePriser,
    andrePriser,
    priserTotal: bragePriser + andrePriser,
    internasjonaleAvtaler,
    filmRettigheter,
    lydbokAvtaler,
  };
}

export function kravForOppgradering(
  fraTier: Tier,
  data: TierBerregnetData,
): TierKravStatus {
  const til = naestTier(fraTier);
  if (!til) {
    return { kriterier: [], klart: false };
  }

  switch (til) {
    case 'C': {
      const utgivelser: TierKriterium = {
        navn: 'Utgivelser',
        naadd: data.antallUtgivelser,
        krav: 10,
      };
      const inntekt: TierKriterium = {
        navn: 'Total inntekt',
        naadd: data.totalInntekt,
        krav: 500_000,
      };
      const klart =
        utgivelser.naadd >= utgivelser.krav || inntekt.naadd >= inntekt.krav;
      return { kriterier: [utgivelser, inntekt], klart };
    }
    case 'B': {
      const nominasjonKr: TierKriterium = {
        navn: 'Brage-nominasjon',
        naadd: data.brageNominasjoner,
        krav: 1,
      };
      const salgKr: TierKriterium = {
        navn: 'Beste enkeltbok-salg',
        naadd: data.bestselger,
        krav: 50_000,
      };
      const klart =
        data.brageNominasjoner >= 1 || data.bestselger >= 50_000;
      return { kriterier: [nominasjonKr, salgKr], klart };
    }
    case 'A': {
      const bragePris: TierKriterium = {
        navn: 'Brage-pris',
        naadd: data.bragePriser,
        krav: 1,
      };
      const priser2: TierKriterium = {
        navn: 'Litterære priser totalt',
        naadd: data.priserTotal,
        krav: 2,
      };
      const sterke: TierKriterium = {
        navn: 'Bøker med snitt ≥ 5.0',
        naadd: data.sterkeBoker,
        krav: 5,
      };
      const klart =
        data.bragePriser >= 1 ||
        data.priserTotal >= 2 ||
        data.sterkeBoker >= 5;
      return { kriterier: [bragePris, priser2, sterke], klart };
    }
    case 'S': {
      const bestselgere: TierKriterium = {
        navn: `Bestselgere (≥ ${BESTSELGER_GRENSE.toLocaleString('nb-NO')} salg)`,
        naadd: data.bestselgere,
        krav: 5,
      };
      const priser5: TierKriterium = {
        navn: 'Litterære priser totalt',
        naadd: data.priserTotal,
        krav: 5,
      };
      const internasjonale: TierKriterium = {
        navn: 'Internasjonale oversettelses-avtaler',
        naadd: data.internasjonaleAvtaler,
        krav: 3,
      };
      const filmRettigheter: TierKriterium = {
        navn: 'Film-rettigheter solgt',
        naadd: data.filmRettigheter,
        krav: 3,
      };
      const lydbokAvtaler: TierKriterium = {
        navn: 'Lydbok-avtaler signert',
        naadd: data.lydbokAvtaler,
        krav: 5,
      };
      const klart =
        data.bestselgere >= 5 ||
        data.priserTotal >= 5 ||
        data.internasjonaleAvtaler >= 3 ||
        data.filmRettigheter >= 3 ||
        data.lydbokAvtaler >= 5;
      return {
        kriterier: [bestselgere, priser5, internasjonale, filmRettigheter, lydbokAvtaler],
        klart,
      };
    }
    default:
      return { kriterier: [], klart: false };
  }
}

export type TierBonusBeskrivelse = {
  tier: Tier;
  punkter: ReadonlyArray<string>;
};

export const TIER_FORDELER: Record<Tier, ReadonlyArray<string>> = {
  D: ['Småforlags-katalog tilgjengelig', 'Lave forskudd'],
  C: [
    'Marked og PR-etasjen åpnes',
    'Trykkeri-kjelleren åpnes',
    'Aftenposten-helgebilag-kampanjer låst opp',
    'Salgs-bonus 1.1× (større distribusjon)',
    'Premium-manus dukker oftere opp i innboksen',
  ],
  B: [
    'Takterrassen åpnes',
    'TV2 God Morgen Norge-bookinger låst opp',
    'Salgs-bonus 1.25× (anerkjent navn)',
    'Bonnier-tier-forfattere kommer i innboksen',
  ],
  A: [
    'Salgs-bonus 1.5× (distribusjonsavtale med ARK)',
    'Stor-tre-tier-manus dominerer innboksen',
  ],
  S: [
    'Salgs-bonus 2.0× (Cappelen/Aschehoug-tier)',
    'Stortrekant-status i hele bransjen',
  ],
};

export const SPILLER_TIER_SALGSBONUS: Record<Tier, number> = {
  D: 1.0,
  C: 1.1,
  B: 1.25,
  A: 1.5,
  S: 2.0,
};

export const KANAL_TIER_KRAV: Record<string, Tier> = {
  'tv2-morgen': 'B',
  aftenposten: 'C',
};

export function kanalErLaast(kanalId: string, spillerTier: Tier): boolean {
  const krav = KANAL_TIER_KRAV[kanalId];
  if (!krav) return false;
  return TIER_ORDEN.indexOf(spillerTier) < TIER_ORDEN.indexOf(krav);
}

export type TierManusMix = Record<Tier, number>;

export const MANUS_MIX_PER_TIER: Record<Tier, TierManusMix> = {
  D: { D: 0.80, C: 0.15, B: 0.05, A: 0.0, S: 0.0 },
  C: { D: 0.50, C: 0.35, B: 0.12, A: 0.03, S: 0.0 },
  B: { D: 0.30, C: 0.35, B: 0.25, A: 0.08, S: 0.02 },
  A: { D: 0.15, C: 0.25, B: 0.30, A: 0.20, S: 0.10 },
  S: { D: 0.05, C: 0.15, B: 0.25, A: 0.30, S: 0.25 },
};

export function velgManusTier(
  spillerTier: Tier,
  rng: () => number,
): Tier {
  const mix = MANUS_MIX_PER_TIER[spillerTier];
  const r = rng();
  let kum = 0;
  for (const t of TIER_ORDEN) {
    kum += mix[t];
    if (r < kum) return t;
  }
  return 'D';
}
